import { Select, Table, TableColumnsType } from 'antd';
import QueryString from 'qs';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import {
  getArchivedMessages,
  deleteReceivedMessages,
} from '../../../api/messages.api';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import {
  IGetReceivedMessages,
  IReceivedMessage,
} from '../../../models/messages.model';
import { RootState } from '../../../store/rootReducer';
import Checkbox from '../../../components/Checkbox';
import { setCurrentCampus } from '../../../store/auth.slice';
import moment from 'moment';
import ErrorResponse from '../../../components/ErrorResponse';
import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import SelectCampus from '../../../components/SelectCampus';
import Button from '../../../components/Button';
import _ from 'lodash';
import Pagination from '../../../components/Pagination';
import config from '../../../lib/config';
import useAuth from '../../../hooks/useAuth';
import ConfirmModal from '../../../components/modals/ConfirmModal';

const MessageArchive: FC = () => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { groups } = useSelector((state: RootState) => state.group);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetReceivedMessages>();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();
  
  const [data, setData] = useState<IReceivedMessage[]>([]);
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  // 0 = no filtering
  const [groupNo, setGroupNo] = useState<number>(0);
  const [field, setField] = useState<string>('name');
  const [keyword, setKeyword] = useState<string>('');
  const [selects, setSelects] = useState<number[]>([]);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  useEffect(() => {
    execute(getArchivedMessages());
  }, []);

  useEffect(() => {
    if (res && !isLoading) {
      // query to state
      const _param: any = {};

      if (query.page) {
        setPage(Number(query.page));
        _param.page = Number(query.page);
      }
      if (query.campusCode) {
        dispatch(setCurrentCampus(String(query.campusCode)));
        _param.campusCode = String(query.campusCode);
      }
      if (query.groupNo) {
        setGroupNo(Number(query.groupNo));
        _param.groupNo = Number(query.groupNo);
      }
      if (query.field && query.keyword) {
        setField(String(query.field));
        setKeyword(String(query.keyword));
        _param.field = String(query.field);
        _param.keyword = String(query.keyword);
      }

      setParam(_param);

      // data filtering
      let _data: IReceivedMessage[] = _.cloneDeep(res.data);

      if (query.campusCode) {
        _data = _data.filter(
          (item) => item.senderCampusCode === String(query.campusCode),
        );
      }
      if (query.groupNo) {
        _data = _data.filter(
          (item) => item.senderGroupNo === Number(query.groupNo),
        );
      }
      if (query.field && query.keyword) {
        let _field = 'title';
        if (String(query.field) === 'name') {
          _field = 'senderKorName';
        }

        _data = _data.filter(
          (item: any) => item[_field].indexOf(String(query.keyword)) !== -1,
        );
      }

      setData(_data);
    }
  }, [res, isLoading, location]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      execute(getArchivedMessages());
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  const onSetSelects = (messageCode: number) => {
    if (selects.includes(messageCode)) {
      setSelects(selects.filter((item: any) => item !== messageCode));
      setIsSelectAll(false);
    } else {
      setSelects([...selects, messageCode]);
    }
  };

  const onSelectAll = () => {
    if (isSelectAll) {
      setSelects([]);
      setIsSelectAll(false);
    } else {
      setSelects(
        res?.data
          ?.slice(page * 10 - 10, page * 10)
          .map((item) => item.messageCode) || [],
      );
      setIsSelectAll(true);
    }
  };

  const onConfirmDelete = () => {
    if (!selects.length) {
      return infoNotification(t('message.archive.message1'));
    }

    setConfirmDeleteVisible(true);
  }

  const onDelete = () => {
    deleteExecute(deleteReceivedMessages(selects));
  };

  const onSearch = () => {
    const param: any = { page: 1 };

    if (currentCampus) param.campusCode = currentCampus;
    if (groupNo !== 0) param.groupNo = groupNo;
    if (keyword) {
      param.field = field;
      param.keyword = keyword;
    }

    setParam(param);
    history.push(`/message/archive?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<IReceivedMessage> = [
    {
      title: (
        <Checkbox
          checked={isSelectAll}
          onClickCapture={(e) => {
            e.stopPropagation();
            onSelectAll();
          }}
          text=""
        />
      ),
      dataIndex: 'messageCode',
      key: 'messageCode',
      align: 'center',
      width: '5%',
      render: (value: number) => {
        let checked = false;
        if (selects.includes(value) || isSelectAll) checked = true;

        return (
          <Checkbox
            checked={checked}
            onClickCapture={(e) => {
              e.stopPropagation();
              onSetSelects(value);
            }}
            text=""
          />
        );
      },
    },
    {
      title: t('message.archive.column1'),
      dataIndex: 'senderKorName',
      key: 'senderKorName',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.senderKorName?.localeCompare(b.senderKorName),
      render: (value, record) => (
        <>
          ({record.senderCampusKorName}) {record.senderKorName}
        </>
      ),
    },
    {
      title: t('common.column.title'),
      key: 'title',
      dataIndex: 'title',
      ellipsis: true,
      sorter: (a, b) => a.title?.localeCompare(b.title),
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('message.archive.column2'),
      key: 'sendDate',
      dataIndex: 'sendDate',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.sendDate?.localeCompare(b.sendDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD HH:mm'),
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-message-big.svg"
      parentMenu={t('common.menu.message')}
      currentMenu={t('message.archive.title1')}
      onlyTitle={true}
    />	
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('common.column.group')}</label>
            <div>
              <Select
                placeholder={t('common.column.group')}
                value={groupNo}
                onChange={(value) => setGroupNo(value)}
              >
                <Select.Option key={0} value={0}>
                  {t('common.column.group')}
                </Select.Option>
                {groups?.map((item: any) => {
                  return (
                    <Select.Option key={item.groupNo} value={item.groupNo}>
                      {i18n.language === 'ko' ? item.korName : item.engName}
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </li>
          <li>
            <div>
              <Select
                value={field}
                onChange={(value) => setField(value)}
              >
                <Select.Option value="name">{t('message.archive.column5')}</Select.Option>
                <Select.Option value="title">{t('common.column.title')}</Select.Option>
              </Select>
            </div>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('common.text.input')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading || deleteIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={deleteError} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{data.length}</b>{t('common.text.unit')}</div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/message/write')}
              text={t('message.archive.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/message/archive/${
                      record.messageCode
                    }?${QueryString.stringify(param)}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="messageCode"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={data.length}
              page={page}
              param={param}
              path="/message/archive"
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic gray"
              onClick={onConfirmDelete}
              text={t('common.button.delete')}
            />
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/message/write')}
              text={t('message.archive.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>

      {confirmDeleteVisible && (
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDelete}
          titleText={t('message.archive.title2')}
          messageText={t('message.archive.text1')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </div>
    </>
  );
};

export default MessageArchive;
